import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  ViewChild,
  ElementRef
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf } from '@angular/cdk/scrolling';
import { Subscription } from 'rxjs';

import { ProjectUploadService } from '../../../../services/project-upload.service';
import { UploadResponse, ErrorLogEntry } from '../../../../models/project-upload.model';
import { MatTooltip } from '@angular/material/tooltip';
import { TruncatePipe } from '../../../../pipes/truncate.pipe';

@Component({
  selector: 'frmg-error-in-files-dialog',
  templateUrl: './error-in-files-dialog.component.html',
  styleUrls: ['./error-in-files-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatTooltip, CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll, CdkVirtualForOf, TruncatePipe]
})
export class ErrorInFilesDialogComponent implements AfterViewInit, OnDestroy {
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;
  @ViewChild('scrollSentinel', { static: false }) sentinelRef!: ElementRef<HTMLElement>;

  logs: ErrorLogEntry[] = [];
  total = 0;
  page = 1;
  loading = false;

  private observer?: IntersectionObserver;
  private readonly sub = new Subscription();

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: { projectId: string; file: UploadResponse },
    private dialogRef: MatDialogRef<ErrorInFilesDialogComponent>,
    private uploadService: ProjectUploadService,
    private cdr: ChangeDetectorRef
  ) {}

  ngAfterViewInit(): void {
    this.loadMore();
    this.setupIntersectionObserver();
  }

  private setupIntersectionObserver(): void {
    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !this.loading && this.logs.length < this.total) {
        this.page++;
        this.loadMore();
      }
    });

    const sentinel = document.querySelector('#scroll-sentinel');
    if (sentinel) {
      this.observer.observe(sentinel);
    }
  }

  private loadMore(): void {
    this.loading = true;
    this.cdr.markForCheck();

    this.uploadService.getErrorLogs(this.data.projectId, this.data.file.upload_id, { page: this.page })
      .subscribe({
        next: (response) => {
          this.logs = [...this.logs, ...response.logs];
          this.total = response.logs.length;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdr.markForCheck();
        }
      });
  }

  objectValues(obj: Record<string, string> = {}): string[] {
    return Object.values(obj || {});
  }

  close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.observer?.disconnect();
  }

  get file(): UploadResponse {
    return this.data?.file;
  }
}
